<template>
  <CModal :title="title" :color="color" :show.sync="showModal" size="xl" class="basic-modal-color">
    <CRow class="mb-2">
      <CCol md="12">
        <div class="history-header">
          <div class="history-left">
            <span><strong>Fecha:</strong> {{ schedule.scheduled_date | date }} {{ formatTime(schedule.scheduled_time) }}</span>
            <span><strong>Tipo:</strong> {{ getTypeLabel(schedule.message_type) }}</span>
            <span>
              <strong>Estado:</strong>
              <span :class="['wa-badge', 'wa-badge--' + schedule.status]">{{ getStatusLabel(schedule.status) }}</span>
            </span>
          </div>

          <div class="history-right">
            <span class="wa-metric"><strong>Destinatarios:</strong> {{ schedule.recipient_count || 0 }}</span>
            <span class="wa-metric">Enviados: {{ schedule.total_sent || 0 }}</span>
            <span class="wa-metric">Entregados: {{ schedule.total_delivered || 0 }}</span>
            <span class="wa-metric">Leidos: {{ schedule.total_read || 0 }}</span>
            <span class="wa-metric">Error: {{ schedule.total_error || 0 }}</span>
          </div>
        </div>
      </CCol>
    </CRow>

    <CRow class="mb-3">
      <CCol md="12" class="d-flex align-items-end filters-row">
        <CInput
          v-model="searchText"
          placeholder="Buscar"
          class="filters-search"
        />
        <CSelect :value.sync="statusFilter" :options="statusOptions" class="filters-status ml-2" />
      </CCol>
    </CRow>

    <CRow>
      <CCol md="12">
        <CDataTable
          :items="filteredItems"
          :fields="fields"
          hover
          striped
          border
          small
          fixed
          pagination
          :noItemsView="{ noResults: 'No se encontraron registros', noItems: 'Sin historial del envio' }"
        >
          <template #position="{ index }">
            <td class="text-center">{{ index + 1 }}</td>
          </template>

          <template #name="{ item }">
            <td>{{ item.guest_name }}</td>
          </template>

          <template #phone="{ item }">
            <td class="text-center">{{ item.phone }}</td>
          </template>

          <template #stage_request="{ item }">
            <td class="text-center wa-cell">
              <CIcon v-if="isNoSent(item.status)" name="cil-clock" class="wa-icon wa-icon--pending" />
              <span v-else>-</span>
            </td>
          </template>

          <template #stage_sent="{ item }">
            <td class="text-center wa-cell">
              <CIcon v-if="isSent(item.status)" name="cil-check" class="wa-icon wa-icon--sent" />
              <span v-else>-</span>
            </td>
          </template>

          <template #stage_delivered="{ item }">
            <td class="text-center wa-cell">
              <span v-if="isDelivered(item.status)" class="wa-checks wa-checks--delivered">
                <CIcon name="cil-check" class="wa-icon" />
                <CIcon name="cil-check" class="wa-icon wa-icon--second" />
              </span>
              <span v-else>-</span>
            </td>
          </template>

          <template #stage_read="{ item }">
            <td class="text-center wa-cell">
              <span v-if="isRead(item.status)" class="wa-checks wa-checks--read">
                <CIcon name="cil-check" class="wa-icon" />
                <CIcon name="cil-check" class="wa-icon wa-icon--second" />
              </span>
              <span v-else>-</span>
            </td>
          </template>

          <template #stage_error="{ item }">
            <td class="text-center wa-cell">
              <CIcon v-if="isError(item.status)" name="cil-warning" class="wa-icon wa-icon--failed" />
              <span v-else>-</span>
            </td>
          </template>
        </CDataTable>
      </CCol>
    </CRow>

    <template #footer>
      <CButton color="secondary" @click="closeModal">Cerrar</CButton>
    </template>
  </CModal>
</template>

<script>
import store from '@/store'
import useMessageTypeLabel from '@/composables/useMessageTypeLabel'
import wsHistory from '@/services/events_whatsapp_history'
import wsGuests from '@/services/events_guests'

const { getMessageTypeLabel } = useMessageTypeLabel()

export default {
  name: 'ScheduledSendDetailModal',
  data() {
    return {
      showModal: false,
      title: 'Detalle del envio programado',
      color: 'info',
      eventId: this.$route.params.id,
      schedule: {},
      items: [],
      statusFilter: '',
      searchText: '',
      statusOptions: [
        { label: 'Todos', value: '' },
        { label: 'No enviado', value: 'request' },
        { label: 'Enviado', value: 'sent' },
        { label: 'Entregado', value: 'delivered' },
        { label: 'Leido', value: 'read' },
        { label: 'Error', value: 'failed' }
      ]
    }
  },
  computed: {
    fields() {
      return [
        { key: 'position', label: 'N°', _style: 'width: 50px;' },
        { key: 'name', label: 'Invitado' },
        { key: 'phone', label: 'Contacto', _style: 'width: 120px;' },
        { key: 'stage_request', label: 'No enviado', _style: 'width: 95px;' },
        { key: 'stage_sent', label: 'Enviado', _style: 'width: 80px;' },
        { key: 'stage_delivered', label: 'Entregado', _style: 'width: 95px;' },
        { key: 'stage_read', label: 'Leido', _style: 'width: 75px;' },
        { key: 'stage_error', label: 'Error', _style: 'width: 70px;' }
      ]
    },
    filteredItems() {
      const normalizedItems = this.items.map(i => ({
        ...i,
        name: i.guest_name || ''
      }))

      const query = (this.searchText || '').toLowerCase().trim()

      return normalizedItems.filter(i => {
        if (!this.matchStatusFilter(i.status)) {
          return false
        }

        if (!query) {
          return true
        }

        const name = (i.guest_name || '').toLowerCase()
        const phone = (i.phone || '').toLowerCase()
        return name.includes(query) || phone.includes(query)
      })
    }
  },
  methods: {
    async showDetail(schedule, batchId) {
      this.schedule = schedule || {}
      this.items = []
      this.statusFilter = ''
      this.searchText = ''
      this.showModal = true

      if (!batchId) {
        await this.loadRecipientsFromSchedule(this.schedule)
        return
      }

      this.loading()
      const response = await wsHistory.getBatchDetail(this.eventId, batchId)
      if (response.type === 'success') {
        this.items = response.data || []
        await this.appendMissingRecipients(this.schedule)
      }
      this.loaded()
    },
    closeModal() {
      this.showModal = false
      this.items = []
      this.schedule = {}
      this.statusFilter = ''
      this.searchText = ''
    },
    parseRecipientIds(value) {
      if (Array.isArray(value)) {
        return value.map(v => parseInt(v, 10)).filter(v => Number.isInteger(v) && v > 0)
      }

      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value)
          return this.parseRecipientIds(parsed)
        } catch (e) {
          return []
        }
      }

      return []
    },
    async loadRecipientsFromSchedule(schedule) {
      const recipientIds = this.parseRecipientIds(schedule.recipient_ids)
      const response = await wsGuests.get({
        event_id: this.eventId,
        is_confirm: '',
        event_table_id: ''
      })

      if (response.type !== 'success') {
        return
      }

      const guests = (response.data || []).filter(g => g && g.phone)
      const allowed = new Set(recipientIds)
      const usedPhones = new Set()

      this.items = guests
        .filter(g => allowed.size === 0 || allowed.has(g.id))
        .filter(g => {
          const phoneKey = this.normalizePhoneForCompare(g.phone)
          if (!phoneKey || usedPhones.has(phoneKey)) {
            return false
          }
          usedPhones.add(phoneKey)
          return true
        })
        .map(g => ({
          id: `guest_${g.id}`,
          guest_id: g.id,
          guest_name: g.name || g.guest_name || '',
          phone: g.phone || '',
          status: 'request'
        }))
    },
    async appendMissingRecipients(schedule) {
      const recipientIds = this.parseRecipientIds(schedule.recipient_ids)
      if (!recipientIds.length) {
        return
      }

      const response = await wsGuests.get({
        event_id: this.eventId,
        is_confirm: '',
        event_table_id: ''
      })

      if (response.type !== 'success') {
        return
      }

      const byGuestId = new Set(this.items.map(i => parseInt(i.guest_id, 10)).filter(v => Number.isInteger(v)))
      const byPhone = new Set(this.items.map(i => this.normalizePhoneForCompare(i.phone)).filter(v => !!v))
      const allowed = new Set(recipientIds)
      const guests = (response.data || []).filter(g => {
        if (!g || !g.phone || !allowed.has(g.id) || byGuestId.has(g.id)) {
          return false
        }

        const phoneKey = this.normalizePhoneForCompare(g.phone)
        if (!phoneKey || byPhone.has(phoneKey)) {
          return false
        }

        byPhone.add(phoneKey)
        return true
      })

      const missing = guests.map(g => ({
        id: `guest_${g.id}`,
        guest_id: g.id,
        guest_name: g.name || g.guest_name || '',
        phone: g.phone || '',
        status: 'request'
      }))

      this.items = [...this.items, ...missing]
    },
    normalizePhoneForCompare(phone) {
      const digits = String(phone || '').replace(/\D/g, '')
      if (!digits) return ''
      return digits.length === 10 ? `521${digits}` : digits
    },
    normalizeStatus(value) {
      return (value || '').toString().toLowerCase().trim()
    },
    isNoSent(status) {
      const normalized = this.normalizeStatus(status)
      return normalized === 'request' || normalized === 'pending'
    },
    isSent(status) {
      const normalized = this.normalizeStatus(status)
      return normalized === 'send' || normalized === 'sent' || normalized === 'delivered' || normalized === 'read'
    },
    isDelivered(status) {
      const normalized = this.normalizeStatus(status)
      return normalized === 'delivered' || normalized === 'read'
    },
    isRead(status) {
      return this.normalizeStatus(status) === 'read'
    },
    isError(status) {
      const normalized = this.normalizeStatus(status)
      return normalized === 'failed' || normalized === 'error'
    },
    matchStatusFilter(status) {
      if (!this.statusFilter) {
        return true
      }

      if (this.statusFilter === 'request') {
        return this.isNoSent(status)
      }

      if (this.statusFilter === 'sent') {
        return this.isSent(status)
      }

      if (this.statusFilter === 'delivered') {
        return this.isDelivered(status)
      }

      if (this.statusFilter === 'read') {
        return this.isRead(status)
      }

      if (this.statusFilter === 'failed') {
        return this.isError(status)
      }

      return true
    },
    getTypeLabel(type) {
      return getMessageTypeLabel(type)
    },
    getStatusLabel(status) {
      switch (status) {
        case 'scheduled': return 'Programado'
        case 'executing': return 'Ejecutando'
        case 'queued': return 'En cola'
        case 'executed': return 'Ejecutado'
        case 'cancelled': return 'Cancelado'
        case 'failed': return 'Error'
        default: return status
      }
    },
    formatTime(value) {
      if (!value) return ''
      return String(value).substring(0, 5)
    },
    loading() { store.commit('loading') },
    loaded() { store.commit('loaded') }
  }
}
</script>

<style scoped>
.history-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.history-left,
.history-right {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.wa-cell {
  white-space: nowrap;
}

.wa-icon {
  font-size: 14px;
  vertical-align: -2px;
}

.wa-icon--pending,
.wa-icon--sent {
  color: #6c757d;
}

.wa-icon--failed {
  color: #dc3545;
}

.wa-checks {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.wa-icon--second {
  margin-left: -6px;
}

.wa-checks--delivered .wa-icon {
  color: #6c757d;
}

.wa-checks--read .wa-icon {
  color: #34b7f1;
}

.wa-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.wa-badge--scheduled {
  background: #fff3cd;
  color: #856404;
}

.wa-badge--executing,
.wa-badge--queued {
  background: #cce5ff;
  color: #004085;
}

.wa-badge--executed {
  background: #d4edda;
  color: #155724;
}

.wa-badge--cancelled,
.wa-badge--failed {
  background: #f8d7da;
  color: #721c24;
}

.filters-row {
  max-width: 560px;
}

.filters-search {
  flex: 1 1 auto;
}

.filters-status {
  flex: 0 0 170px;
}
</style>
